import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Button } from './ui/button';
import { Bell, ChevronDown, FileText, KeyRound, LogOut, User } from 'lucide-react';
import { Badge } from './ui/badge';
import exampleImage from '../src/assets/logo.png';
import { AppContext } from '../src/App';
import { useContext, useState } from 'react';
import { useAuth } from './AuthContext';
import { useNavigate } from 'react-router-dom';
import { VIEW_TO_PATH } from '@/routes/config';
// Import from local UI folder, NOT @radix-ui directly
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from './ui/dropdown-menu';
import { Avatar, AvatarFallback } from './ui/avatar';
import { apiClient } from '@/Utils/apiClient';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { set } from 'date-fns';

export function Header() {
  const { orgConfig, setCurrentView } = useContext(AppContext);
  // Destructure logout if available in your AuthContext, otherwise handle manually
  const { user, token, signOut } = useAuth();
  const navigate = useNavigate();
  const [signoutLoading, setSignoutLoading] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);

  // Handlers
  const handleProfileClick = () => {
    setCurrentView('profile');
    const targetPath = VIEW_TO_PATH['profile'];
    if (targetPath) navigate(targetPath);
    else navigate('/profile');
  };

  const handleLogout = async () => {
    setSignoutLoading(true);
    try {
      const response = await apiClient(`auth/signout`, {
        method: 'POST',
        token
      });
      const data = await response.json();
      if (!data.success) throw new Error(data.error || 'Request failed');
      toast.success(data?.message || 'Signed out successfully');
      handleLogoutAction();
    } catch (err: any) {

      if (err.response?.status === 401 || err.status === 401) {
        handleLogoutAction();
        toast.success("Signed out successfully");
      } else {
        toast.error(err.message || "Failed to sign out");
      }
    } finally {
      setSignoutLoading(false);
    }

  }
  const getUserInitials = () => {
    const firstInitial = user?.name?.[0] || '';
    const lastInitial = user?.lastName?.[0] || '';
    const initials = (firstInitial + lastInitial).toUpperCase();
    return initials || 'U'; // Fallback to 'U' if no names exist
  };

  const maskEmail = (email: string) => {
    if (!email) return "";

    const [localPart, domainPart] = email.split("@");
    if (!localPart || !domainPart) return email;

    // Split domain into name + extension (e.g., gmail.com → gmail + .com)
    const domainSplit = domainPart.split(".");
    const domainName = domainSplit[0];
    const domainExt = domainSplit.slice(1).join("."); // handles .co.in etc.

    // Local part masking (first 2 chars + **** + last char)
    const firstTwo = localPart.slice(0, 2);
    const lastChar = localPart.slice(-1);
    const maskedLocal = `${firstTwo}${"*".repeat(localPart.length - 3)}${lastChar}`;

    // Domain masking (first 2 chars + ***)
    const maskedDomain = `${domainName?.slice(0, 2)}****`;

    return `${maskedLocal}@${maskedDomain}.${domainExt}`;
  };

  const handleLogoutAction = () => {
    signOut();
    setCurrentView("");
    const targetPath = VIEW_TO_PATH['signIn'];
    if (targetPath) navigate(targetPath);
    else navigate('/signIn');
  };
  const handleResetPassword = async () => {
    setIsResetting(true);
    try {
      const email = user?.email; // Declare a variable named 'email'

      if (!email) return toast.error('User email not found', {
        description: 'Cannot send reset email without a valid email address.',
      });

      const response = await fetch(
        `${import.meta.env.VITE_API_URL}auth/password/reset`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email }),
        },
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Request failed");
      }

      toast.success('Password reset email sent!', {
        description: 'Please check your inbox to continue.',
      });
    } catch (err: any) {
      toast.error(err.message || 'Failed to send reset email');
    } finally {
      setIsResetting(false);
      setShowResetDialog(false);
    }
  };

  return (
    <>
      <header 
      className={`h-16 bg-background border-b border-border flex items-center justify-between ${
        orgConfig?.managedBy?.logoPadding
        ? orgConfig?.managedBy?.logoPadding : "pl-6 pr-6"}`} >
        {/* Logo and Title */}
        <div className="flex items-center gap-4">
          <div><img src={orgConfig?.managedBy?.logo} 
          alt={`${orgConfig?.managedBy?.name} Logo`} 
          className={orgConfig?.managedBy?.logoClassName ? orgConfig?.managedBy?.logoClassName : "w-32 h-auto"} /></div>
          {/* <div className="w-8 h-8 bg-primary rounded flex items-center justify-center">
          <FileText className="w-5 h-5 text-primary-foreground" />
        </div> */}
          {/* <span className="text-foreground font-medium text-lg">MAS Medical</span> */}
        </div>

        {/* Right Side Controls */}
        <div className="flex items-center gap-4">
          {/* Notifications */}
          {/* <div className="relative">
          <Button variant="ghost" size="icon" className="relative">
            <Bell className="w-5 h-5 text-muted-foreground" />
            <Badge 
              variant="destructive" 
              className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center text-xs"
            >
              16
            </Badge>
          </Button>
        </div> */}

          {/* Language/Environment Selector */}
          {/* <Select defaultValue="ottl">
          <SelectTrigger className="w-20">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ottl">OTTL</SelectItem>
            <SelectItem value="prod">PROD</SelectItem>
            <SelectItem value="dev">DEV</SelectItem>
          </SelectContent>
        </Select> */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="h-auto py-2 px-3 hover:bg-sidebar-accent transition-colors flex items-center gap-3 focus-visible:ring-0 focus-visible:ring-offset-0"
              >
                {/* Avatar */}
                <Avatar className="w-8 h-8 flex-shrink-0">
                  {/* Add AvatarImage here if you have a user image URL */}
                  {/* <AvatarImage src={user?.image} /> */}
                  <AvatarFallback className="bg-primary text-primary-foreground text-sm font-medium">
                    {getUserInitials()}

                  </AvatarFallback>
                </Avatar>


              </Button>
            </DropdownMenuTrigger>

            {/* Dropdown Content */}
            <DropdownMenuContent align="end" className="w-56">
              {/* <DropdownMenuLabel>My Account</DropdownMenuLabel>
            <DropdownMenuSeparator /> */}
              <DropdownMenuLabel className="font-normal">
                <div className="flex flex-col space-y-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium leading-none">{user?.name} {user?.lastName}</p>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <p className="text-xs leading-none">{maskEmail(user?.email || '')}</p>
                  </div>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleProfileClick} className="cursor-pointer focus:outline-none">
                <User className="mr-2 h-4 w-4" />
                <span>Profile</span>
              </DropdownMenuItem>

              <DropdownMenuItem
                onClick={(e) => {
                  e.preventDefault();
                  setShowResetDialog(true);
                }}
                className="cursor-pointer focus:outline-none"
              >
                <KeyRound className="mr-2 h-4 w-4" />
                <span>Reset Password</span>
              </DropdownMenuItem>

              {/* <DropdownMenuSeparator /> */}

              <DropdownMenuItem onClick={handleLogout} disabled={signoutLoading} className="cursor-pointer text-red-600 focus:text-red-600 focus:outline-none">
                <LogOut className="mr-2 h-4 w-4" />
                <span>Sign Out</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>
      <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>
              Are you sure you want to send a password reset link?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" className="" onClick={() => setShowResetDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleResetPassword} disabled={isResetting}>
              {isResetting ? 'Sending...' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}